import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { LogIn, RefreshCw, ShieldCheck, UserPlus } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { PortalUserContext } from "../../shared/types";
import { isAuthConfigured, loginRequest } from "./authConfig";
import { setAccessTokenProvider } from "../services/apiClient";
import { getPortalContext } from "../services/portalService";
import { clearTelemetryUser, setTelemetryUser } from "../services/telemetry";

export type DashboardRole =
  | "HealthDashboard.Reader"
  | "HealthDashboard.Operator"
  | "HealthDashboard.Admin"
  | "StationAnalytics.User"
  | "StationAnalytics.AccessAdmin"
  | "Marketing.Analytics"
  | "Portal.exeMembers";

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthUserContextValue {
  roles: DashboardRole[];
  portalContext?: PortalUserContext;
  userLabel: string;
  signOut: () => void;
  hasAnyRole: (roles: DashboardRole[]) => boolean;
}

const AuthUserContext = createContext<AuthUserContextValue>({
  roles: [],
  portalContext: undefined,
  userLabel: "Authenticated user",
  signOut: () => {},
  hasAnyRole: () => false
});

function readRoles(claims: unknown): DashboardRole[] {
  if (!claims || typeof claims !== "object" || !("roles" in claims)) {
    return [];
  }

  const roles = (claims as { roles?: unknown }).roles;
  return Array.isArray(roles)
    ? roles.filter((role): role is DashboardRole => (
      typeof role === "string" &&
      (role.startsWith("HealthDashboard.") || role.startsWith("StationAnalytics.") || role.startsWith("Marketing.") || role.startsWith("Portal."))
    ))
    : [];
}

function readRolesFromAccessToken(accessToken: string): DashboardRole[] {
  try {
    const [, payload] = accessToken.split(".");
    if (!payload) {
      return [];
    }

    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="))) as unknown;
    return readRoles(decoded);
  } catch {
    return [];
  }
}

export function useAuthUser(): AuthUserContextValue {
  return useContext(AuthUserContext);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { accounts, instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = instance.getActiveAccount() || accounts[0];
  const activeAccountKey = activeAccount?.homeAccountId || activeAccount?.localAccountId || activeAccount?.username || "";
  const [apiAuthState, setApiAuthState] = useState<"idle" | "loading" | "ready" | "interaction_required" | "error">("idle");
  const [apiAuthError, setApiAuthError] = useState<string>();
  const [apiAuthRetryKey, setApiAuthRetryKey] = useState(0);
  const [roles, setRoles] = useState<DashboardRole[]>(() => readRoles(activeAccount?.idTokenClaims));
  const [portalContext, setPortalContext] = useState<PortalUserContext>();

  useEffect(() => {
    const account = activeAccount;

    if (!isAuthConfigured || !account) {
      setAccessTokenProvider(undefined);
      setApiAuthState("idle");
      setApiAuthError(undefined);
      setRoles([]);
      setPortalContext(undefined);
      clearTelemetryUser();
      return;
    }

    let isMounted = true;
    const acquireApiTokenResponse = async () => {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account
      });

      if (!response.accessToken) {
        throw new Error("Microsoft Entra ID did not return an API access token for the configured scope.");
      }

      return response;
    };

    const acquireApiToken = async (): Promise<string> => {
      return (await acquireApiTokenResponse()).accessToken;
    };

    setApiAuthState("loading");
    setApiAuthError(undefined);
    setAccessTokenProvider(acquireApiToken);

    acquireApiTokenResponse()
      .then(async (response) => {
        if (!isMounted) {
          return;
        }

        setAccessTokenProvider(acquireApiToken);
        const tokenRoles = readRolesFromAccessToken(response.accessToken);
        const idTokenRoles = readRoles(response.idTokenClaims);
        const accountRoles = readRoles(account.idTokenClaims);
        const resolvedRoles = tokenRoles.length ? tokenRoles : idTokenRoles.length ? idTokenRoles : accountRoles;
        const nextPortalContext = await getPortalContext();
        setPortalContext(nextPortalContext);
        setTelemetryUser(
          nextPortalContext.userPrincipalName || nextPortalContext.email || account.username,
          nextPortalContext.entraObjectId || account.localAccountId
        );
        setRoles(nextPortalContext.roles.length ? readRoles({ roles: nextPortalContext.roles }) : resolvedRoles);
        setApiAuthState("ready");
      })
      .catch((error: unknown) => {
        if (!isMounted) {
          return;
        }

        setAccessTokenProvider(undefined);
        setPortalContext(undefined);
        clearTelemetryUser();
        setRoles(readRoles(account.idTokenClaims));
        if (error instanceof InteractionRequiredAuthError) {
          setApiAuthState("interaction_required");
          setApiAuthError("Additional permission is required to access the dashboard API.");
          return;
        }

        setApiAuthState("error");
        setApiAuthError(error instanceof Error ? error.message : "Unable to acquire dashboard API access token.");
      });

    return () => {
      isMounted = false;
    };
  }, [activeAccountKey, apiAuthRetryKey, instance]);

  const requestApiAuthorization = useCallback(() => {
    if (!activeAccount) {
      void instance.loginRedirect(loginRequest).catch((error: unknown) => {
        setApiAuthError(error instanceof Error ? error.message : "Unable to start Microsoft Entra sign-in.");
        setApiAuthState("error");
      });
      return;
    }

    void instance.acquireTokenRedirect({
      ...loginRequest,
      account: activeAccount
    }).catch((error: unknown) => {
      setApiAuthError(error instanceof Error ? error.message : "Unable to start Microsoft Entra authorization.");
      setApiAuthState("error");
    });
  }, [activeAccount, instance]);

  const retryApiAccess = useCallback(() => {
    setApiAuthError(undefined);
    setApiAuthState("loading");
    setApiAuthRetryKey((value) => value + 1);
  }, []);

  const signIn = useCallback(() => {
    void instance.loginRedirect(loginRequest);
  }, [instance]);

  const signUp = useCallback(() => {
    void instance.loginRedirect({
      ...loginRequest,
      prompt: "create"
    });
  }, [instance]);

  const signOut = useCallback(() => {
    setAccessTokenProvider(undefined);
    void instance.logoutRedirect();
  }, [instance]);

  const userLabel = useMemo(() => activeAccount?.name || activeAccount?.username || "Authenticated user", [activeAccount]);
  const authUserContext = useMemo<AuthUserContextValue>(() => ({
    roles,
    portalContext,
    userLabel,
    signOut,
    hasAnyRole: (allowedRoles) => roles.some((role) => allowedRoles.includes(role))
  }), [portalContext, roles, signOut, userLabel]);

  if (!isAuthConfigured) {
    return (
      <div className="auth-screen">
        <div className="auth-panel">
          <ShieldCheck size={30} aria-hidden="true" />
          <span>Authentication Setup Required</span>
          <h1>Microsoft Entra ID is not configured.</h1>
          <p>Set VITE_AZURE_AD_CLIENT_ID and VITE_AZURE_AD_TENANT_ID before enabling protected access.</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="auth-screen">
        <div className="auth-panel">
          <ShieldCheck size={30} aria-hidden="true" />
          <span>Gen7Fuel Data Centre</span>
          <h1>Gen7Fuel Data Portal</h1>
          <p>Sign in or complete the approved sign-up flow to request access.</p>
          <div className="auth-actions">
            <button
              className="primary-button auth-button"
              disabled={inProgress !== "none"}
              onClick={signIn}
              type="button"
            >
              <LogIn size={17} aria-hidden="true" />
              Sign in
            </button>
            <button
              className="primary-button auth-button"
              disabled={inProgress !== "none"}
              onClick={signUp}
              type="button"
            >
              <UserPlus size={17} aria-hidden="true" />
              Create account
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (apiAuthState === "loading" || apiAuthState === "idle") {
    return (
      <div className="auth-screen">
        <div className="auth-panel">
          <ShieldCheck size={30} aria-hidden="true" />
          <span>DataOps Health Command Center</span>
          <h1>Preparing API access</h1>
          <p>Validating your Microsoft Entra ID session for the dashboard API.</p>
        </div>
      </div>
    );
  }

  if (apiAuthState === "interaction_required") {
    return (
      <div className="auth-screen">
        <div className="auth-panel">
          <ShieldCheck size={30} aria-hidden="true" />
          <span>DataOps Health Command Center</span>
          <h1>API permission required</h1>
          <p>{apiAuthError || "Authorize the dashboard API before loading monitoring data."}</p>
          <button
            className="primary-button auth-button"
            disabled={inProgress !== "none"}
            onClick={requestApiAuthorization}
            type="button"
          >
            <LogIn size={17} aria-hidden="true" />
            Authorize API access
          </button>
        </div>
      </div>
    );
  }

  if (apiAuthState === "error") {
    return (
      <div className="auth-screen">
        <div className="auth-panel">
          <ShieldCheck size={30} aria-hidden="true" />
          <span>DataOps Health Command Center</span>
          <h1>API connection unavailable</h1>
          <p>{apiAuthError || "Unable to load your portal access context from the dashboard API."}</p>
          <div className="auth-actions">
            <button
              className="primary-button auth-button"
              disabled={inProgress !== "none"}
              onClick={retryApiAccess}
              type="button"
            >
              <RefreshCw size={17} aria-hidden="true" />
              Retry API connection
            </button>
            <button
              className="primary-button auth-button secondary-button"
              disabled={inProgress !== "none"}
              onClick={requestApiAuthorization}
              type="button"
            >
              <LogIn size={17} aria-hidden="true" />
              Reauthorize
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthUserContext.Provider value={authUserContext}>
      {children}
    </AuthUserContext.Provider>
  );
}
